import { useState, useCallback, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import {
  Stack,
  Menu,
  MenuItem,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Table,
  TableHead,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';

import { Edit } from '@mui/icons-material';

import MainContainer from '../../components/MainContainer';
import MainHeader from '../../components/MainHeader';
import TableRowSkeleton from '../../components/TableRowSkeleton';
import WhatsAppModalCompany from '../../components/CompanyWhatsapps';
import ConfirmationModal from '../../components/ConfirmationModal';
import ForbiddenPage from '../../components/ForbiddenPage';

import { AuthContext } from '../../context/Auth/AuthContext';
import useCompanies from '../../hooks/useCompanies';
import api from '../../services/api';
import QrcodeModal from '../../components/QrcodeModal';
import { i18n } from '../../translate/i18n';
import toastError from '../../toasts/toastError';
import { getStyled } from './styles';

const AllConnections = () => {
  const theme = useTheme();
  const classes = getStyled(theme);
  const { user } = useContext(AuthContext);
  const { list } = useCompanies();
  const [loadingWhatsapp, setLoadingWhatsapp] = useState(true);
  const [whats, setWhats] = useState([]);
  const [whatsAppModalOpen, setWhatsAppModalOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedWhatsApp, setSelectedWhatsApp] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [filterConnections, setFilterConnections] = useState([]);
  const [companyWhatsApps, setCompanyWhatsApps] = useState(null);
  const confirmationModalInitialState = {
    action: '',
    title: '',
    message: '',
    whatsAppId: '',
    open: false,
  };
  const [confirmModalInfo, setConfirmModalInfo] = useState(
    confirmationModalInitialState,
  );

  const navigate = useNavigate();
  if (!user.super) {
    navigate('/tickets');
  }

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const { data } = await api.get('/whatsapp/all/?session=0');
        setWhats(data);
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingWhatsapp(false);
      }
    };
    fetchSession();
  }, []);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const companyList = await list();
        setCompanies(companyList);
      } catch (e) {
        toastError(
          `Não foi possível carregar a lista de registros. ${e.error}`,
        );
      }
    };
    loadCompanies();
  }, [list]);

  const handleOpenWhatsAppModal = (whatsappsFilter, comp) => {
    setSelectedWhatsApp(null);
    setWhatsAppModalOpen(true);
    if (whatsappsFilter?.length > 0) {
      setFilterConnections(whatsappsFilter);
      setCompanyWhatsApps(comp);
    }
  };

  const handleCloseWhatsAppModal = useCallback(() => {
    setWhatsAppModalOpen(false);
    setSelectedWhatsApp(null);
    setFilterConnections([]);
    setCompanyWhatsApps(null);
  }, [setSelectedWhatsApp, setWhatsAppModalOpen]);

  const handleCloseQrModal = useCallback(() => {
    setSelectedWhatsApp(null);
    setQrModalOpen(false);
  }, [setQrModalOpen, setSelectedWhatsApp]);

  const handleSubmitConfirmationModal = async () => {
    if (confirmModalInfo.action === 'disconnect') {
      try {
        await api.delete(
          `/whatsappsession/admin/${confirmModalInfo.whatsAppId}`,
        );
        toastSuccess(i18n.t('connections.toasts.disconnected'));
      } catch (err) {
        toastError(err);
      }
    }

    if (confirmModalInfo.action === 'delete') {
      try {
        await api.delete(`/whatsapp/${confirmModalInfo.whatsAppId}`);
        toastSuccess(i18n.t('connections.toasts.deleted'));
      } catch (err) {
        toastError(err);
      }
    }

    setConfirmModalInfo(confirmationModalInitialState);
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title={confirmModalInfo.title}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleSubmitConfirmationModal}
      >
        {confirmModalInfo.message}
      </ConfirmationModal>
      <QrcodeModal
        open={qrModalOpen}
        onClose={handleCloseQrModal}
        whatsAppId={!whatsAppModalOpen && selectedWhatsApp?.id}
      />
      <WhatsAppModalCompany
        open={whatsAppModalOpen}
        onClose={handleCloseWhatsAppModal}
        filteredWhatsapps={filterConnections}
        companyInfos={companyWhatsApps}
        whatsAppId={!qrModalOpen && selectedWhatsApp?.id}
      />
      {user.profile === 'user' ? (
        <ForbiddenPage />
      ) : (
        <>
          <Paper sx={classes.mainPaper} variant='outlined'>
            <MainHeader>
              <Stack>
                <Typography
                  variant='h5'
                  style={{
                    fontWeight: 'bold',
                    marginLeft: '15px',
                    marginTop: '10px',
                  }}
                  gutterBottom
                >
                  {i18n.t('connections.title')}
                </Typography>
                <Typography
                  style={{ marginTop: '-10px', marginLeft: '15px' }}
                  variant='caption'
                >
                  Conecte seus canais de atendimento para receber mensagens e
                  iniciar conversas com seus clientes.
                </Typography>
              </Stack>
            </MainHeader>
            <Stack
              sx={{
                overflowY: 'auto',
                padding: '20px',
                backgroundColor: 'rgb(244 244 244 / 53%)',
                borderRadius: '5px',
              }}
            >
              <Paper>
                <Table size='small'>
                  <TableHead sx={classes.TableHead}>
                    <TableRow>
                      <TableCell align='center'>{i18n.t('Cliente')}</TableCell>
                      <TableCell align='center'>
                        {i18n.t('Conexões conectadas')}
                      </TableCell>
                      <TableCell align='center'>
                        {i18n.t('Conexões desconectadas')}
                      </TableCell>
                      <TableCell align='center'>
                        {i18n.t('Total de Conexões')}
                      </TableCell>
                      <TableCell align='center'>
                        {i18n.t('connections.table.actions')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingWhatsapp ? (
                      <TableRowSkeleton />
                    ) : (
                      <>
                        {companies?.length > 0 &&
                          companies.map((company) => (
                            <TableRow
                              key={company.id}
                              sx={{
                                backgroundColor:
                                  theme.custom.colors.defaultBackground,
                                color: theme.custom.colors.text,
                              }}
                            >
                              <TableCell align='center'>
                                {company?.name}
                              </TableCell>
                              <TableCell align='center'>
                                {whats?.length &&
                                  whats.filter(
                                    (item) =>
                                      item?.companyId === company?.id &&
                                      item?.status === 'CONNECTED',
                                  ).length}
                              </TableCell>
                              <TableCell align='center'>
                                {whats?.length &&
                                  whats.filter(
                                    (item) =>
                                      item?.companyId === company?.id &&
                                      item?.status !== 'CONNECTED',
                                  ).length}
                              </TableCell>
                              <TableCell align='center'>
                                {whats?.length &&
                                  whats.filter(
                                    (item) => item?.companyId === company?.id,
                                  ).length}
                              </TableCell>
                              {user.profile === 'admin' && (
                                <TableCell align='center'>
                                  <IconButton
                                    size='small'
                                    onClick={() =>
                                      handleOpenWhatsAppModal(
                                        whats.filter(
                                          (item) =>
                                            item?.companyId === company?.id,
                                        ),
                                        company,
                                      )
                                    }
                                  >
                                    <Edit />
                                  </IconButton>
                                </TableCell>
                              )}
                            </TableRow>
                          ))}
                        <TableRow sx={classes.TableHead}>
                          <TableCell style={{ color: '#fff' }} align='center'>
                            {i18n.t('Total')}
                          </TableCell>
                          <TableCell style={{ color: '#fff' }} align='center'>
                            {whats?.length &&
                              whats.filter(
                                (item) => item?.status === 'CONNECTED',
                              ).length}
                          </TableCell>
                          <TableCell style={{ color: '#fff' }} align='center'>
                            {whats?.length &&
                              whats.filter(
                                (item) => item?.status !== 'CONNECTED',
                              ).length}
                          </TableCell>
                          <TableCell style={{ color: '#fff' }} align='center'>
                            {whats?.length && whats.length}
                          </TableCell>
                          {user.profile === 'admin' && (
                            <TableCell
                              style={{ color: '#fff' }}
                              align='center'
                            ></TableCell>
                          )}
                        </TableRow>
                      </>
                    )}
                  </TableBody>
                </Table>
              </Paper>
            </Stack>
          </Paper>
        </>
      )}
    </MainContainer>
  );
};

export default AllConnections;
